'use client';
import React, { useState } from 'react';
import { TESTING_SECTIONS, SEQUENCE_QUIZ, TestingSection, TestingStep } from '../data/testingData';
import { ChevronDown, ChevronUp, CheckCircle, XCircle, BookOpen, Zap, AlertTriangle, ChevronRight } from 'lucide-react';

type Tab = 'guide' | 'sequence' | 'quiz';

const typeConfig = {
  dead: { label: 'Dead Test', colour: 'text-blue-700', bg: 'bg-blue-100', icon: '🔒' },
  live: { label: 'Live Test', colour: 'text-red-700', bg: 'bg-red-100', icon: '⚡' },
  visual: { label: 'Visual', colour: 'text-green-700', bg: 'bg-green-100', icon: '👁️' },
};

function StepCard({ step }: { step: TestingStep }) {
  const [expanded, setExpanded] = useState(false);
  const tc = typeConfig[step.type];

  return (
    <div className="border border-border rounded-xl bg-white overflow-hidden">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full text-left px-4 py-3.5 flex items-center gap-3 hover:bg-muted/30 transition-colors"
      >
        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
          {step.order}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-foreground">{step.title}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tc.bg} ${tc.colour}`}>
              {tc.icon} {tc.label}
            </span>
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{step.description}</p>
        </div>
        {expanded ? <ChevronUp size={16} className="flex-shrink-0 text-muted-foreground" /> : <ChevronDown size={16} className="flex-shrink-0 text-muted-foreground" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 border-t border-border bg-slate-50/50">
          <div className="pt-3 space-y-3">
            {/* Procedure */}
            <div>
              <p className="text-xs font-semibold text-foreground mb-2">Procedure</p>
              <ol className="space-y-1.5">
                {step.procedure.map((proc, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-foreground">
                    <span className="flex-shrink-0 w-4 h-4 rounded-full bg-primary/15 text-primary text-[10px] font-bold flex items-center justify-center mt-0.5">
                      {idx + 1}
                    </span>
                    <span className="leading-relaxed">{proc}</span>
                  </li>
                ))}
              </ol>
            </div>

            {/* Acceptance Criteria */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-green-700 mb-1 flex items-center gap-1">
                <CheckCircle size={12} /> Acceptance Criteria
              </p>
              <p className="text-xs text-green-800 leading-relaxed">{step.acceptanceCriteria}</p>
            </div>

            {/* Tip */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-xs font-semibold text-amber-700 mb-1 flex items-center gap-1">
                💡 Tip
              </p>
              <p className="text-xs text-amber-800 leading-relaxed">{step.tip}</p>
            </div>

            {/* Regulation */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Regulation:</span>
              <span className="text-xs font-mono font-medium text-primary bg-primary/10 px-2 py-0.5 rounded">{step.regulation}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SectionCard({ section }: { section: TestingSection }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className={`rounded-xl border-2 ${section.borderColour} overflow-hidden`}>
      <button
        onClick={() => setExpanded(!expanded)}
        className={`w-full text-left px-5 py-4 flex items-center gap-4 ${section.bgColour} hover:opacity-90 transition-opacity`}
      >
        <span className="text-2xl flex-shrink-0">{section.icon}</span>
        <div className="flex-1 min-w-0">
          <h3 className={`font-semibold text-sm ${section.colour}`}>{section.title}</h3>
          <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{section.summary}</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <span className={`text-xs px-2 py-0.5 rounded-full ${section.bgColour} ${section.colour} border ${section.borderColour} font-medium`}>
            {section.steps.length} steps
          </span>
          {expanded ? <ChevronUp size={16} className={section.colour} /> : <ChevronDown size={16} className={section.colour} />}
        </div>
      </button>

      {expanded && (
        <div className="p-4 space-y-3 bg-white">
          <p className="text-xs text-muted-foreground leading-relaxed border-l-2 border-primary/30 pl-3">{section.summary}</p>
          {section.steps.map(step => (
            <StepCard key={step.id} step={step} />
          ))}
        </div>
      )}
    </div>
  );
}

function SequenceGuide() {
  const allSteps = [
    { label: 'Visual Inspection', type: 'visual' as const, desc: 'Check installation before any testing' },
    { label: 'Continuity of CPCs', type: 'dead' as const, desc: 'Verify all protective conductors are intact' },
    { label: 'Continuity of Ring Final', type: 'dead' as const, desc: 'Verify ring circuits are complete' },
    { label: 'Insulation Resistance', type: 'dead' as const, desc: 'Test insulation integrity at 500V DC' },
    { label: 'Polarity (Dead)', type: 'dead' as const, desc: 'Verify single-pole devices in line conductor' },
    { label: 'Earth Fault Loop Impedance', type: 'live' as const, desc: 'Measure Zs at circuit extremities' },
    { label: 'Polarity (Live Verification)', type: 'live' as const, desc: 'Confirm correct polarity at accessories' },
    { label: 'RCD Testing', type: 'live' as const, desc: 'Test trip times at ½×, 1×, and 5× In' },
    { label: 'Functional Testing', type: 'live' as const, desc: 'Operate all switches, sockets, and controls' },
  ];

  return (
    <div className="space-y-3">
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle size={16} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-amber-700 mb-1">Key Rule: Dead Tests Before Live Tests</p>
          <p className="text-xs text-amber-800 leading-relaxed">Always complete all dead tests (supply isolated) before energising the installation for live tests. This is a safety requirement and the correct sequence per BS 7671 and Guidance Note 3.</p>
        </div>
      </div>

      <div className="flex gap-4 text-xs mb-2">
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-blue-200 inline-block" /> Dead Test</div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-200 inline-block" /> Live Test</div>
        <div className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-200 inline-block" /> Visual</div>
      </div>

      <div className="space-y-2">
        {allSteps.map((step, idx) => {
          const tc = typeConfig[step.type];
          return (
            <div key={idx} className="flex items-start gap-3 bg-white border border-border rounded-xl p-3.5">
              <div className="flex flex-col items-center gap-1 flex-shrink-0">
                <span className="w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center">
                  {idx + 1}
                </span>
                {idx < allSteps.length - 1 && <div className="w-0.5 h-4 bg-border" />}
              </div>
              <div className="flex-1 min-w-0 pt-0.5">
                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                  <span className="text-sm font-medium text-foreground">{step.label}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${tc.bg} ${tc.colour}`}>
                    {tc.icon} {tc.label}
                  </span>
                </div>
                <p className="text-xs text-muted-foreground">{step.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function SequenceQuiz() {
  const [currentQ, setCurrentQ] = useState(0);
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [answers, setAnswers] = useState<{ correct: boolean }[]>([]);
  const [done, setDone] = useState(false);

  const q = SEQUENCE_QUIZ[currentQ];

  const handleSelect = (idx: number) => {
    if (selectedOption !== null) return;
    setSelectedOption(idx);
  };

  const handleNext = () => {
    if (selectedOption === null) return;
    const newAnswers = [...answers, { correct: selectedOption === q.correctIndex }];
    setAnswers(newAnswers);
    if (currentQ + 1 < SEQUENCE_QUIZ.length) {
      setCurrentQ(currentQ + 1);
      setSelectedOption(null);
    } else {
      setDone(true);
    }
  };

  const reset = () => {
    setCurrentQ(0);
    setSelectedOption(null);
    setAnswers([]);
    setDone(false);
  };

  if (done) {
    const score = answers.filter(a => a.correct).length;
    const pct = Math.round((score / SEQUENCE_QUIZ.length) * 100);
    return (
      <div className="text-center py-8">
        <div className={`inline-flex flex-col items-center gap-2 px-8 py-6 rounded-2xl border-2 mb-6 ${pct >= 80 ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
          <span className="text-4xl font-bold text-foreground">{pct}%</span>
          <span className="text-sm text-muted-foreground">{score} / {SEQUENCE_QUIZ.length} correct</span>
        </div>
        <button onClick={reset} className="px-6 py-2.5 bg-primary text-white rounded-xl text-sm font-medium hover:bg-primary/90 transition-colors">
          Retake Quiz
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <span className="text-xs text-muted-foreground">Question {currentQ + 1} of {SEQUENCE_QUIZ.length}</span>
        <div className="h-1.5 flex-1 mx-4 bg-muted rounded-full overflow-hidden">
          <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${(currentQ / SEQUENCE_QUIZ.length) * 100}%` }} />
        </div>
      </div>
      <div className="bg-white border border-border rounded-xl p-5 mb-4">
        <p className="text-sm font-medium text-foreground leading-relaxed mb-5">{q.question}</p>
        <div className="space-y-2.5">
          {q.options.map((opt, idx) => {
            let style = 'border-border bg-white hover:bg-muted/50 cursor-pointer';
            if (selectedOption !== null) {
              if (idx === q.correctIndex) style = 'border-green-400 bg-green-50 cursor-default';
              else if (idx === selectedOption) style = 'border-red-400 bg-red-50 cursor-default';
              else style = 'border-border bg-white opacity-50 cursor-default';
            }
            return (
              <button key={idx} onClick={() => handleSelect(idx)} className={`w-full text-left px-4 py-3 rounded-lg border-2 transition-all flex items-start gap-3 ${style}`}>
                <span className="flex-shrink-0 w-5 h-5 rounded-full border-2 border-current flex items-center justify-center text-xs font-bold mt-0.5">
                  {String.fromCharCode(65 + idx)}
                </span>
                <span className="text-sm leading-relaxed">{opt}</span>
                {selectedOption !== null && idx === q.correctIndex && <CheckCircle size={15} className="ml-auto flex-shrink-0 text-green-600 mt-0.5" />}
                {selectedOption !== null && idx === selectedOption && idx !== q.correctIndex && <XCircle size={15} className="ml-auto flex-shrink-0 text-red-500 mt-0.5" />}
              </button>
            );
          })}
        </div>
      </div>
      {selectedOption !== null && (
        <>
          <div className={`rounded-xl border p-4 mb-4 ${selectedOption === q.correctIndex ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
            <p className={`text-xs font-semibold mb-1 ${selectedOption === q.correctIndex ? 'text-green-700' : 'text-amber-700'}`}>
              {selectedOption === q.correctIndex ? '✓ Correct!' : '✗ Incorrect'}
            </p>
            <p className="text-xs text-foreground leading-relaxed">{q.explanation}</p>
          </div>
          <button onClick={handleNext} className="w-full py-3 bg-primary text-white rounded-xl font-medium text-sm hover:bg-primary/90 transition-colors flex items-center justify-center gap-2">
            {currentQ + 1 < SEQUENCE_QUIZ.length ? <>Next Question <ChevronRight size={16} /></> : <>See Results <CheckCircle size={16} /></>}
          </button>
        </>
      )}
    </div>
  );
}

export default function TestingVerificationClient() {
  const [activeTab, setActiveTab] = useState<Tab>('guide');

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'guide', label: 'Testing Guide', icon: <BookOpen size={14} /> },
    { id: 'sequence', label: 'Test Sequence', icon: <Zap size={14} /> },
    { id: 'quiz', label: 'Quick Quiz', icon: <CheckCircle size={14} /> },
  ];

  return (
    <div className="max-w-3xl mx-auto px-4 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground mb-2">Testing & Initial Verification</h1>
        <p className="text-muted-foreground text-sm leading-relaxed">
          Learn the process, sequence, and requirements for inspecting and testing electrical installations in accordance with BS 7671 and Guidance Note 3.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-muted rounded-xl p-1 mb-6">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-3 rounded-lg text-xs font-medium transition-all duration-150 ${activeTab === tab.id ? 'bg-white text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'}`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Guide Tab */}
      {activeTab === 'guide' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-blue-700 mb-1">📋 About Initial Verification</p>
            <p className="text-xs text-blue-800 leading-relaxed">
              Initial verification must be carried out on every new installation and on additions or alterations to existing installations before they are put into service. It consists of inspection and testing to verify the installation complies with BS 7671.
            </p>
          </div>
          {TESTING_SECTIONS.map(section => (
            <SectionCard key={section.id} section={section} />
          ))}
        </div>
      )}

      {/* Sequence Tab */}
      {activeTab === 'sequence' && (
        <div>
          <div className="mb-4">
            <h2 className="text-base font-semibold text-foreground mb-1">Correct Testing Sequence</h2>
            <p className="text-xs text-muted-foreground">The order in which tests must be carried out for initial verification per BS 7671 and Guidance Note 3.</p>
          </div>
          <SequenceGuide />
        </div>
      )}

      {/* Quiz Tab */}
      {activeTab === 'quiz' && (
        <div>
          <div className="mb-4">
            <h2 className="text-base font-semibold text-foreground mb-1">Testing Sequence Quiz</h2>
            <p className="text-xs text-muted-foreground">Test your knowledge of the inspection and testing sequence and procedures.</p>
          </div>
          <SequenceQuiz />
        </div>
      )}
    </div>
  );
}
